/* Zana explains the page, and you point (design: ConsoleZanaExplain,
   ConsoleZanaGuide, ConsoleAskStat). A page lists what on it is worth
   explaining (its "points", each worded from the live records) and marks the
   elements with data-zx. Zana then either walks through them — a numbered pin
   on each, a ring and a note on the current one — or, in pointing mode, puts a
   box over each so the owner can click one and ask about it.

   The ? beside a stat (StatQ) is the same explanation, opened in place: a
   short answer that streams in, "Explain more" for what it means, why it
   matters and what to watch, and "Ask more" to carry it into the chat. */
import React, { useEffect, useRef, useState } from 'react';
import { useGo, IcX, PageKey } from '../kit';
import { reducedMotion, useBoxes, zxSel } from './box';

export interface ExplainPoint {
  key: string;
  label: string;
  /** Three lines at most: the short answer. */
  short: string;
  /** Words in `short` to mark. */
  mark?: string;
  means?: string;
  matters?: string;
  watch?: string;
  /** Follow-up questions for the chat. */
  follow?: string[];
  open?: { page: PageKey; search?: string; label: string };
}

export interface ExplainState { mode: 'explain' | 'point'; step: number; pick: number }

/** The short answer with its key words marked. */
export const Marked: React.FC<{ text: string; mark?: string; on?: boolean }> = ({ text, mark, on = true }) => {
  const i = mark ? text.indexOf(mark) : -1;
  if (!mark || i < 0) return <>{text}</>;
  return <>{text.slice(0, i)}<mark className={'zmark' + (on ? ' on' : '')}>{mark}</mark>{text.slice(i + mark.length)}</>;
};

/** Pins, the ring and the note over the page body (explain), or a box over
    each point to click (point). */
export const ExplainLayer: React.FC<{ points: ExplainPoint[]; ex: ExplainState | null; set: (s: ExplainState | null) => void }> = ({ points, ex, set }) => {
  const layer = useRef<HTMLDivElement>(null);
  const boxes = useBoxes(layer, points.map((p) => zxSel(p.key)), !!ex);
  const go = useGo();
  const curNote = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!ex || ex.mode !== 'explain' || !ex.step) return;
    const el = document.querySelector(zxSel(points[ex.step - 1]?.key || ''));
    // A tall section shows its top, so the note under its start is in view.
    const tall = !!el && el.getBoundingClientRect().height > window.innerHeight * 0.5;
    el?.scrollIntoView?.({ block: tall ? 'start' : 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ex?.mode, ex?.step]);
  if (!ex) return null;
  const host = layer.current?.getBoundingClientRect();
  return (
    <div className="zx-layer" ref={layer} aria-hidden={ex.mode === 'point' ? undefined : true}>
      {points.map((p, i) => {
        const b = boxes[i];
        if (!b) return null;
        const n = i + 1;
        const style = { left: b.left - 4, top: b.top - 4, width: b.width + 8, height: b.height + 8 };
        if (ex.mode === 'point') {
          return (
            <div key={p.key} className={'zx-box' + (ex.pick === n ? ' zpicked' : '')} style={style}>
              <button type="button" className="zpick" data-zlabel={`Ask about ${p.label}`} aria-label={`Ask Zana about ${p.label}`} onClick={() => set({ ...ex, pick: n })} />
            </div>
          );
        }
        const cur = ex.step === n;
        /* The note sits under the element; above it when there is no room
           below; inside it, near the bottom of the screen, when it is tall. */
        const hostH = host?.height || window.innerHeight;
        const room = b.top + b.height + 240 < hostH;
        const up = !room && b.top > 250;
        const inside = !room && !up ? { top: Math.max(16, Math.min(b.height - 60, hostH - b.top - 250)), left: 16 } : null;
        return (
          <div key={p.key} className={'zx-box' + (cur ? ' zon' : '')} style={style}>
            <button type="button" className={'zpin' + (n < ex.step || !ex.step ? ' done' : '')} aria-label={`Step ${n}: ${p.label}`} onClick={() => set({ ...ex, step: n })}>{n}</button>
            {cur && (
              <div className={'znote2' + (up ? ' up' : '') + (inside ? ' in' : '')} ref={curNote} role="dialog" aria-label={`Zana · ${p.label}`} style={inside || undefined}>
                <span className="sm" style={{ fontWeight: 700, color: 'var(--z-ink)' }}>Zana · {p.label}</span>
                <b style={{ fontSize: 15 }}><Marked text={p.short} mark={p.mark} /></b>
                {p.means && <span style={{ color: 'var(--ink2)' }}>{p.means}</span>}
                <div className="row" style={{ gap: 6 }}>
                  <span className="sm" style={{ color: 'var(--muted)' }}>{n} of {points.length}</span>
                  <span style={{ flex: 1 }} />
                  {p.open && <a className="btn s b-line" {...go(p.open.page, p.open.search)}>{p.open.label}</a>}
                  {n > 1 && <button type="button" className="btn s b-ghost" onClick={() => set({ ...ex, step: n - 1 })}>Back</button>}
                  <button type="button" className="btn s" style={{ background: 'var(--z)', color: '#fff' }} onClick={() => set({ ...ex, step: n >= points.length ? 0 : n + 1 })}>
                    {n >= points.length ? 'Done' : 'Next'}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/** The panel while Zana explains or the owner points. */
export const ExplainBody: React.FC<{ points: ExplainPoint[]; ex: ExplainState; set: (s: ExplainState | null) => void; onAsk: (q: string, about: string) => void }> = ({ points, ex, set, onAsk }) => {
  const picked = ex.pick ? points[ex.pick - 1] : null;
  return (
    <div className="zin-a" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className="zx-seg" role="tablist" aria-label="How Zana helps">
        <button type="button" role="tab" aria-selected={ex.mode === 'explain'} className={ex.mode === 'explain' ? 'on' : ''} onClick={() => set({ mode: 'explain', step: ex.step || 1, pick: 0 })}>Zana explains</button>
        <button type="button" role="tab" aria-selected={ex.mode === 'point'} className={ex.mode === 'point' ? 'on' : ''} onClick={() => set({ mode: 'point', step: 0, pick: 0 })}>I point</button>
      </div>
      {ex.mode === 'explain' ? (
        <div className="zcard" style={{ gap: 6 }}>
          <b style={{ fontSize: 16 }}>Explain this page to me</b>
          <span className="sm" style={{ color: 'var(--muted)' }}>I’ll walk you through {points.length} thing{points.length === 1 ? '' : 's'} on the page. I mark each one where it sits. Click a step to jump to it.</span>
          <div style={{ marginTop: 4 }}>
            {points.map((p, i) => (
              <button key={p.key} type="button" className={'zx-step' + (ex.step === i + 1 ? ' on' : '')} onClick={() => set({ ...ex, step: i + 1 })}>
                <span className={'tk-dot' + (ex.step > i + 1 || !ex.step ? ' done' : ex.step === i + 1 ? ' cur' : '')}>{ex.step > i + 1 || !ex.step ? '✓' : i + 1}</span>
                <span style={ex.step === i + 1 ? { fontWeight: 600, color: 'var(--ink)' } : { color: 'var(--muted)' }}>{p.label}</span>
              </button>
            ))}
          </div>
          <div className="zacts">
            {ex.step ? <button type="button" className="btn s b-line" onClick={() => set({ ...ex, step: 0 })}>Stop explaining</button>
              : <button type="button" className="btn s b-line" onClick={() => set({ ...ex, step: 1 })}>Start again</button>}
            <button type="button" className="btn s b-ghost" style={{ color: 'var(--muted)' }} onClick={() => set(null)}>Close</button>
          </div>
        </div>
      ) : (
        <>
          <div className="zcard" style={{ gap: 6 }}>
            <b style={{ fontSize: 16 }}>Point at anything on the page</b>
            <span className="sm" style={{ color: 'var(--muted)' }}>Click a number, a row or a card and ask about it. Esc stops pointing.</span>
          </div>
          {picked && (
            <div className="zcard zin-a" style={{ gap: 8 }}>
              <span className="chip c-z" style={{ alignSelf: 'flex-start' }}>{picked.label}</span>
              <b style={{ fontSize: 15 }}>What does this mean?</b>
              <span style={{ color: 'var(--ink2)', lineHeight: '22px' }}><Marked text={picked.short} mark={picked.mark} /></span>
              {picked.means && <span className="sm" style={{ color: 'var(--muted)' }}>{picked.means}</span>}
              <div className="zacts">
                <button type="button" className="btn s b-line" onClick={() => set({ mode: 'explain', step: ex.pick, pick: 0 })}>Show me on the page</button>
                <button type="button" className="btn s" style={{ background: 'var(--z)', color: '#fff' }} onClick={() => onAsk(`Tell me more about ${picked.label.toLowerCase()}`, picked.label)}>Ask more</button>
              </div>
            </div>
          )}
          <div className="zacts"><button type="button" className="btn s b-ghost" style={{ color: 'var(--muted)' }} onClick={() => set(null)}>Stop pointing</button></div>
        </>
      )}
    </div>
  );
};

/* ── the ? beside a stat ─────────────────────────────────────────────────── */

const CLOSE_OTHERS = 'trasse:statq';

/** `hover`: a stat that needs no interpreting shows Zana's mark on hover
    instead of a ? (desktop only). */
export const StatQ: React.FC<{ p: ExplainPoint; side?: 'l' | 'r'; hover?: boolean; onAsk: (q: string, about: string) => void }> = ({ p, side = 'l', hover, onAsk }) => {
  const [open, setOpen] = useState(false);
  const [n, setN] = useState(0);
  const [big, setBig] = useState(false);
  const me = useRef(Math.random().toString(36));
  const box = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const other = (e: Event) => { if ((e as CustomEvent).detail !== me.current) setOpen(false); };
    window.addEventListener(CLOSE_OTHERS, other);
    return () => window.removeEventListener(CLOSE_OTHERS, other);
  }, []);
  // The short answer streams in: Zana writes, she does not pop.
  useEffect(() => {
    if (!open) return undefined;
    if (reducedMotion()) { setN(p.short.length); return undefined; }
    setN(0);
    const t = setInterval(() => setN((x) => { if (x >= p.short.length) { clearInterval(t); return x; } return Math.min(p.short.length, x + 5); }), 28);
    return () => clearInterval(t);
  }, [open, p.short]);
  useEffect(() => {
    if (!open) return undefined;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    const away = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    window.addEventListener('keydown', esc, true); document.addEventListener('mousedown', away);
    return () => { window.removeEventListener('keydown', esc, true); document.removeEventListener('mousedown', away); };
  }, [open]);
  const toggle = () => {
    if (!open) window.dispatchEvent(new CustomEvent(CLOSE_OTHERS, { detail: me.current }));
    setBig(false); setOpen(!open);
  };
  const writing = n < p.short.length;
  const shown = p.short.slice(0, n);
  const hasMore = !!(p.means || p.matters || p.watch);
  return (
    <span ref={box} style={{ position: 'static' }}>
      {hover ? (
        <button type="button" className={'hz' + (open ? ' show' : '')} aria-label={`Ask Zana about ${p.label.toLowerCase()}`} aria-expanded={open} onClick={toggle}>
          <span aria-hidden="true" style={{ width: 16, height: 16, borderRadius: 5, background: 'var(--z)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 2.5 }}>
            <i style={{ width: 2.5, height: 4, borderRadius: 3, background: '#fff', display: 'block' }} /><i style={{ width: 2.5, height: 4, borderRadius: 3, background: '#fff', display: 'block' }} />
          </span>
        </button>
      ) : (
        <button type="button" className={'qm' + (open ? ' on' : '')} aria-label={`What does ${p.label.toLowerCase()} mean?`} aria-expanded={open} onClick={toggle}>?</button>
      )}
      {open && (
        <span className={'ann ' + side + (big ? ' big' : '')} role="dialog" aria-label={`Zana · ${p.label}`} style={{ display: 'block' }}>
          <span className="annh">
            <span aria-hidden="true" style={{ width: 18, height: 18, borderRadius: 5, background: 'var(--z)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 3, flex: 'none' }}>
              <i style={{ width: 3, height: 5, borderRadius: 3, background: '#fff', display: 'block' }} /><i style={{ width: 3, height: 5, borderRadius: 3, background: '#fff', display: 'block' }} />
            </span>
            <b>Zana</b><span className="sm" style={{ color: 'var(--muted)' }}>· {p.label}</span>
            <button type="button" className="ibtn" style={{ marginLeft: 'auto', width: 28, height: 28 }} aria-label="Close" onClick={() => setOpen(false)}><IcX size={15} /></button>
          </span>
          <span className="annb" style={{ display: 'block' }} aria-live="polite">
            {writing ? <>{shown}<span className="caret" /></> : <Marked text={p.short} mark={p.mark} />}
          </span>
          {!writing && big && (
            <span className="secs" style={{ display: 'flex' }}>
              {p.means && <div><h4>What it means</h4><p>{p.means}</p></div>}
              {p.matters && <div><h4>Why it matters</h4><p>{p.matters}</p></div>}
              {p.watch && <div><h4>What to watch</h4><p>{p.watch}</p></div>}
            </span>
          )}
          {!writing && big && !!p.follow?.length && (
            <span className="fchips" style={{ display: 'flex' }}>
              {p.follow.map((q) => <button key={q} type="button" onClick={() => { setOpen(false); onAsk(q, p.label); }}>{q}</button>)}
            </span>
          )}
          {!writing && (
            <span className="annf">
              {hasMore && <button type="button" className="btn s b-ghost" onClick={() => setBig(!big)}>{big ? 'Show less' : 'Explain more'}</button>}
              <button type="button" className="btn s" style={{ marginLeft: 'auto', background: 'var(--z)', color: '#fff' }} onClick={() => { setOpen(false); onAsk(`Tell me more about ${p.label.toLowerCase()}`, p.label); }}>Ask more</button>
            </span>
          )}
        </span>
      )}
    </span>
  );
};

type RingBox = { left: number; top: number; width: number; height: number; hostH: number };

function sameBox(prev: RingBox, next: RingBox): boolean {
  return Math.abs(prev.left - next.left) < 0.5 && Math.abs(prev.top - next.top) < 0.5
    && Math.abs(prev.width - next.width) < 0.5 && Math.abs(prev.height - next.height) < 0.5
    && Math.abs(prev.hostH - next.hostH) < 0.5;
}

/** Caption sits on the top edge of the ring. A row's next neighbour is
    immediately under it, so the caption never drops below the ring. */
function captionAbove(box: RingBox, need: number): boolean {
  return box.top > need + 8;
}

/** The thing this answer is about, ringed the way the walkthrough rings a step.
    The layer does not take clicks, so the page under it still works. */
export const PointRing: React.FC<{ el: HTMLElement; label: string }> = ({ el, label }) => {
  const layer = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<RingBox | null>(null);
  useEffect(() => {
    el.scrollIntoView?.({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
  }, [el]);
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      if (!el.isConnected) return;
      const host = layer.current?.parentElement?.getBoundingClientRect();
      const r = el.getBoundingClientRect();
      if (!host || !r.width || !r.height) { raf = requestAnimationFrame(tick); return; }
      const next = { left: r.left - host.left, top: r.top - host.top, width: r.width, height: r.height, hostH: host.height };
      setBox((prev) => (prev && sameBox(prev, next) ? prev : next));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [el]);
  return (
    <div className="zx-layer" ref={layer} aria-hidden="true">
      {box && (
        <div className="zx-box zon" style={{ left: box.left - 4, top: box.top - 4, width: box.width + 8, height: box.height + 8 }}>
          <div className={'zpill' + (captionAbove(box, 28) ? '' : ' in')}>Zana · {label}</div>
        </div>
      )}
    </div>
  );
};

/** A double-clicked element, drawn with the same ring as pointing mode. */
export const PickMark: React.FC<{
  el: HTMLElement;
  label: string;
  onAsk: () => void;
  onExplain: () => void;
  onClose: () => void;
}> = ({ el, label, onAsk, onExplain, onClose }) => {
  const layer = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<RingBox | null>(null);
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const host = layer.current?.parentElement?.getBoundingClientRect();
      const r = el.isConnected ? el.getBoundingClientRect() : null;
      if (!host || !r || !r.width || !r.height) return;
      const next = { left: r.left - host.left, top: r.top - host.top, width: r.width, height: r.height, hostH: host.height };
      setBox((prev) => (prev && sameBox(prev, next) ? prev : next));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [el]);
  return (
    <div className="zx-layer" ref={layer}>
      {box && (
        <div className="zx-box zpicked" style={{ left: box.left - 4, top: box.top - 4, width: box.width + 8, height: box.height + 8 }}>
          <div className={'znote2' + (captionAbove(box, 160) ? ' up' : ' in')} role="dialog" aria-label={`Zana · ${label}`} style={captionAbove(box, 160) ? undefined : { top: 8, left: 8 }}>
            <span className="sm" style={{ fontWeight: 700, color: 'var(--z-ink)' }}>Zana · selected</span>
            <b style={{ fontSize: 15 }}>{label}</b>
            <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
              <button type="button" className="btn s" style={{ background: 'var(--z)', color: '#fff' }} onClick={onAsk}>Ask Zana</button>
              <button type="button" className="btn s b-line" onClick={onExplain}>Explain</button>
              <button type="button" className="btn s b-ghost" style={{ color: 'var(--muted)' }} onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
